// RITA-1585 — Arbeitsfläche einer Vertragsseite mit Handbuch (Skizze 31.10.2026, Bilder 1–5): der äußere Rahmen der
// Seite (Innenabstand, Höchstbreite 1400, zentriert) bleibt dasselbe Element wie bisher; darin oben die Zeile mit der
// Pfadzeile links und dem Knopf „Handbuch“ rechts, darunter die Zeile mit dem Inhalt links und – bei offener Leiste und
// genug Platz – der Handbuch-Spalte rechts. Die Spalte beginnt so auf Höhe der Titelzeile (Punkt A). Mit offener Spalte
// darf der Rahmen um die Spalte breiter werden (1400 + 16 + 380); bei zu wenig Platz legt sich das Handbuch als
// Schublade über den rechten Rand, und der Inhalt behält seine Breite.
//
// Die Platzhalterleiste misst weiter ihren eigenen Rahmen im Inhalt: Nimmt die Spalte ihr die Breite, rückt sie von
// selbst nach oben – mit dem bekannten Hinweis, auch wenn das offene Handbuch der Grund ist (Entscheidung Julia
// 31.10.2026). Das Menü links wird nur schmal, wenn dadurch mehr nebeneinander passt – beim Öffnen oder wenn das
// Fenster bei offener Leiste schmaler wird –, und beim Schließen wieder breit, nur wenn das PRM es selbst schmal
// gemacht hat (Menü-Überschreibung im SettingsContext; die eigene Umschaltung des Nutzers hebt sie auf). Einmal
// schmal bleibt es bis zum Schließen schmal: Nach der Umschaltung wäre die Zeile breiter, und die Regel kippte sonst
// sofort zurück.
//
// Kein `overflow`, `transform` oder `contain` an den Zeilen: Titelzeile und Werkzeuge kleben (sticky), die Unterlage
// des App-Kopfs ist fest (fixed) – beides bezieht sich auf das Fenster.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { useSettingsContext } from '../../../components/settings/SettingsContext';
import useResponsive from '../../../hooks/useResponsive';
import { Handbuchseite } from '../model/handbuecher';
import { HANDBUCH_BREITE_PX, HANDBUCH_LUECKE_PX, menueSchmalLohntSich } from '../model/handbuchplatz';
import useHandbuchLeiste from '../hooks/useHandbuchLeiste';
import useHandbuchPlatz from '../layout/useHandbuchPlatz';
import HandbuchKnopf from './HandbuchKnopf';
import HandbuchSeitenleiste from './HandbuchSeitenleiste';

/** Höchstbreite des Seitenrahmens ohne Handbuch (wie bisher). */
export const RAHMEN_HOECHSTBREITE_PX = 1400;
/**
 * Breite, die das schmale Menü freigibt: NAV.W_DASHBOARD 280 − NAV.W_DASHBOARD_MINI 88 (config-global.ts). Bewusst
 * als Zahl, nicht als Import: config-global zieht die Seiten mit (Importzyklus; in Tests nicht ladbar).
 */
export const MENUE_GEWINN_PX = 280 - 88;

interface Props {
  seite: Handbuchseite;
  /** Die Pfadzeile der Seite (`nav aria-label="Pfadzeile"`); der Knopf steht rechts daneben, nicht darin. */
  pfadzeile: React.ReactNode;
  /** Unterkante des App-Kopfs (gemessen von den festen Vertragsleisten); Spalte und Schublade beginnen darunter. */
  obenAbstandPx: number;
  /** Mindestbreite des Inhalts, die neben der Spalte bleiben muss (Blatt samt Seitenrahmen). */
  mindestInhaltBreitePx: number;
  /**
   * Rahmenbreite, die die seitliche Platzhalterleiste braucht; null ohne Platzhalterleiste oder wenn sie bewusst oben
   * steht. Als Funktion wird der Wert im Moment der Menü-Entscheidung gelesen (die Lage liegt im Speicher).
   */
  leistenBedarfPx: number | null | (() => number | null);
  rahmenRef?: React.Ref<HTMLDivElement>;
  rahmenTestId?: string;
  children: React.ReactNode;
}

export default function HandbuchArbeitsflaeche({
  seite,
  pfadzeile,
  obenAbstandPx,
  mindestInhaltBreitePx,
  leistenBedarfPx,
  rahmenRef,
  rahmenTestId,
  children,
}: Props) {
  const leiste = useHandbuchLeiste(seite);
  const platz = useHandbuchPlatz(mindestInhaltBreitePx);
  const spalteOffen = leiste.offen && platz.darstellung === 'spalte';

  // Menü automatisch schmal: entschieden mit der Messung VOR der Umschaltung; einmal schmal, bis zum Schließen.
  // Schaltet der Nutzer das Menü bei offener Leiste selbst um (Knopf, Einstellungen), ruht die Automatik bis zum
  // Schließen – auch wenn sie noch gar nicht gegriffen hatte (KI-Review !803: Start „mini“, Nutzer stellt breit).
  const { themeLayout, onLayoutUeberschreiben, layoutWahlZaehler } = useSettingsContext();
  const desktop = useResponsive('up', 'lg');
  const [menueAutomatischSchmal, setMenueAutomatischSchmal] = useState(false);
  /** Zählerstand der eigenen Menü-Umschaltungen beim Öffnen; null = Leiste zu. */
  const wahlBeimOeffnenRef = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (!leiste.offen) {
      wahlBeimOeffnenRef.current = null;
      if (menueAutomatischSchmal) setMenueAutomatischSchmal(false);
      return;
    }
    if (wahlBeimOeffnenRef.current === null) wahlBeimOeffnenRef.current = layoutWahlZaehler;
    if (layoutWahlZaehler !== wahlBeimOeffnenRef.current) return;
    if (menueAutomatischSchmal || platz.zeilenBreite === null || !desktop || themeLayout !== 'vertical') return;
    const bedarf = typeof leistenBedarfPx === 'function' ? leistenBedarfPx() : leistenBedarfPx;
    if (menueSchmalLohntSich(platz.zeilenBreite, MENUE_GEWINN_PX, mindestInhaltBreitePx, bedarf)) {
      setMenueAutomatischSchmal(true);
    }
  }, [
    leiste.offen,
    menueAutomatischSchmal,
    platz.zeilenBreite,
    desktop,
    themeLayout,
    layoutWahlZaehler,
    mindestInhaltBreitePx,
    leistenBedarfPx,
  ]);
  useEffect(() => {
    if (!menueAutomatischSchmal) return undefined;
    onLayoutUeberschreiben('mini');
    return () => onLayoutUeberschreiben(null);
  }, [menueAutomatischSchmal, onLayoutUeberschreiben]);

  return (
    <Box
      ref={rahmenRef}
      data-testid={rahmenTestId}
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: spalteOffen
          ? RAHMEN_HOECHSTBREITE_PX + HANDBUCH_LUECKE_PX + HANDBUCH_BREITE_PX
          : RAHMEN_HOECHSTBREITE_PX,
        mx: 'auto',
        minWidth: 0,
      }}
    >
      <Box data-testid="handbuch-pfadzeilen-zeile" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>{pfadzeile}</Box>
        <HandbuchKnopf offen={leiste.offen} onClick={leiste.umschalten} />
      </Box>
      <Box
        ref={platz.zeilenRef}
        data-testid="handbuch-zeile"
        data-verfuegbar={platz.zeilenBreite === null ? undefined : Math.round(platz.zeilenBreite)}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: `${HANDBUCH_LUECKE_PX}px` }}
      >
        <Box sx={{ flex: '1 1 0', minWidth: 0 }}>{children}</Box>
        {/* Eine Stelle im Baum für beide Darstellungen: Beim Wechsel Spalte↔Schublade bleiben Suchtext und Fokus
            erhalten. Die Schublade liegt fest (fixed) und nimmt in der Zeile keinen Platz – auch keine Lücke. */}
        {leiste.offen && (
          <HandbuchSeitenleiste
            darstellung={platz.darstellung}
            obenAbstandPx={obenAbstandPx}
            auswahl={leiste.auswahl}
            onAuswahl={leiste.waehlen}
            onSchliessen={leiste.schliessen}
          />
        )}
      </Box>
    </Box>
  );
}
